import React, { useState, useEffect } from "react";
import logo from "../../assets/azet-logo.png";
import background from "../../assets/login-background.png";
import styles from "./Signin.module.css";
import NewPasswordInput from "../../components/Inputs/NewPasswordInput";
import { AZET } from "../../catalogs/roles";
import usersApi from "../../api/users";
import { store } from "../../redux/store";
import { userSettings } from '../../redux/actions/user'
import { Input, Form, Button, Alert, Modal } from "antd";
import { signIn as amplifySignIn, signOut, confirmSignIn, updatePassword, updateUserAttributes, fetchUserAttributes, fetchAuthSession } from "aws-amplify/auth";
import { signinSuccess } from "../../redux/actions/auth";
import { AZET_BLUE } from "../../helpers/colorHelpers";

const Signin = props => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [modalAlert, setModalAlert] = useState(null);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [repeatNewPassword, setRepeatNewPassword] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [MFAAuthenticationVisible, setMFAAuthenticationVisible] = useState(false);
  const [challengeAnswer, setChallengeAnswer] = useState(null);
  const [isNewPasswordChallenge, setIsNewPasswordChallenge] = useState(false);

  //PRINCIPAL FORM
  const handleSignIn = async () => {
    setAlert(null);
    setLoading(true);

    if (!email || email === "") {
      setAlert({ type: "error", message: "Email is required." });
      setLoading(false);
      return;
    }

    if (!password || password === "") {
      setAlert({ type: "error", message: "Password is required." });
      setLoading(false);
      return;
    }

    try {
      const { isSignedIn, nextStep } = await amplifySignIn({ username: email, password });
      setLoading(false);

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setIsNewPasswordChallenge(true);
        showModal();
        return;
      }
      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        setMFAAuthenticationVisible(true);
        return;
      }
      if (nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
        props.history.push("/mfasetup");
        return;
      }
      if (isSignedIn) {
        const attributes = await fetchUserAttributes();
        if (attributes["custom:IsFirstLogin"] === "1") {
          showModal();
          return;
        }
        const session = await fetchAuthSession();
        store.dispatch(signinSuccess(session));
        if (props.location && props.location.state && props.location.state.from) {
          props.history.push(props.location.state.from);
        } else {
          props.history.push("/app/home");
        }
      }
    } catch (err) {
      setLoading(false);
      if (err.name === "UserAlreadyAuthenticatedException") {
        try {
          await signOut();
        } catch (_) { }
        window.location.reload();
        return;
      }
      if (err.name === "NotAuthorizedException" || err.name === "UserNotFoundException") {
        setAlert({ type: "error", message: "Incorrect username or password." });
      } else {
        setAlert({ type: "error", message: err.message || "Unable to sign in. Please try again." });
      }
    }
  };
  //PRINCIPAL FORM

  //MODAL FIRST LOGIN CHANGE PASSWORD
  const showModal = () => {
    setChangePasswordVisible(true);
  };

  const handleOk = () => {
    onSubmitChangePassword();
  };

  const handleCancel = () => {
    setChangePasswordVisible(false);
    setNewPassword(null);
    setRepeatNewPassword(null);
    setModalAlert(null);
    setIsNewPasswordChallenge(false);
  };

  const onSubmitChangePassword = async () => {
    setAlert(null);
    setModalAlert(null);
    setLoading(true);

    if (newPassword !== repeatNewPassword) {
      setModalAlert({ type: "error", message: "New password does not match" });
      setLoading(false);
      return;
    }

    try {
      if (isNewPasswordChallenge) {
        await confirmSignIn({ challengeResponse: newPassword });
      } else {
        await updatePassword({ oldPassword: password, newPassword });
        await updateUserAttributes({ userAttributes: { "custom:IsFirstLogin": "0" } });
      }
      setPassword(null);
      setNewPassword(null);
      setRepeatNewPassword(null);
      props.history.push("/mfasetup");
    } catch (err) {
      setModalAlert({ type: "error", message: "Unable to change the password. Please contact support." });
    } finally {
      setLoading(false);
    }
  };
  //MODAL FIRST LOGIN CHANGE PASSWORD

  //MODAL MFA
  useEffect(() => {
    if (challengeAnswer) {
      if (challengeAnswer.length == 6) {
        var onlyNumbers = challengeAnswer.match(/^\d+$/);
        if (onlyNumbers != null) {
          setDisabled(false);
          return;
        }
      }
    }
    setDisabled(true);
    return;
  }, [challengeAnswer]);

  const MFAAuthenticationOk = async (e) => {
    e.preventDefault();
    setModalAlert(null);
    setLoading(true);
    try {
      await confirmSignIn({ challengeResponse: challengeAnswer });
      const session = await fetchAuthSession();
      store.dispatch(signinSuccess(session));

      const roles = session.tokens?.accessToken.payload["cognito:groups"];
      if (roles && roles.length > 0 && roles.includes(AZET)) {
        usersApi.getUserPermissions().then(result => {
          store.dispatch(userSettings({ teams: result.teams, permissions: result.permissions }));
        });
      }

      if (props.location && props.location.state && props.location.state.from) {
        props.history.push(props.location.state.from);
      } else {
        props.history.push("/app/home");
      }
    } catch (err) {
      setModalAlert({ type: "error", message: "Unable to verify the token." });
      setLoading(false);
    }
  }

  const MFAAuthenticationCancel = () => {
    setMFAAuthenticationVisible(false);
    setChallengeAnswer(null);
    setModalAlert(null);
  };
  //MODAL MFA

  return (
    <div className={styles.container} style={{ backgroundImage: `url(${background})` }}>
      <div className={styles.content}>
        <div className={styles.logo}>
          <img src={logo} alt="logo" width={125} />
          <div style={{ marginTop: 30, width: 300 }}>
            {alert && (
              <Form.Item>
                <Alert message={alert.message} type={alert.type} closable />
              </Form.Item>
            )}
            <Form.Item>
              <Input
                placeholder="Email"
                name="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onPressEnter={handleSignIn}
              />
            </Form.Item>
            <Form.Item>
              <Input
                placeholder="Password"
                name="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                type="password"
                onPressEnter={handleSignIn}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" block onClick={handleSignIn} loading={loading}>
                Sign in
              </Button>
              <a href="/forgotpassword" style={{ float: "left", color: AZET_BLUE }}>
                Forgot password
              </a>
            </Form.Item>
          </div>
          <div>
            <Modal
              title="Change your password"
              visible={changePasswordVisible}
              onOk={handleOk}
              okButtonProps={{ disabled: disabled }}
              onCancel={handleCancel}
              width={408}
              maskClosable={false}
            >
              <div>
                <div className={styles.logo}>
                  <img src={logo} alt="logo" width={125} />
                  <div style={{ marginTop: 30, width: 300 }}>
                    {modalAlert && (
                      <Form.Item>
                        <Alert message={modalAlert.message} type={modalAlert.type} closable />
                      </Form.Item>
                    )}
                    <Form.Item>
                      <NewPasswordInput
                        placeholder="New Password"
                        onChange={setNewPassword}
                        value={newPassword}
                        onPassValidations={setDisabled} />
                    </Form.Item>
                    <Form.Item>
                      <Input
                        placeholder="Repeat New Password"
                        value={repeatNewPassword}
                        onChange={e => setRepeatNewPassword(e.target.value)}
                        type="password"
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </Modal>
          </div>
          <div>
            <Modal
              title="Multi-Factor Authentication"
              visible={MFAAuthenticationVisible}
              onOk={MFAAuthenticationOk}
              okButtonProps={{ disabled: disabled }}
              onCancel={MFAAuthenticationCancel}
              width={408}
              maskClosable={false}
            >
              <div>
                <div className={styles.logo}>
                  <img src={logo} alt="logo" width={125} />
                  <div style={{ marginTop: 30, width: 300 }}>
                    {modalAlert && (
                      <Form.Item>
                        <Alert message={modalAlert.message} type={modalAlert.type} closable />
                      </Form.Item>
                    )}
                    <div>
                      <label className={styles.mfaInput}>
                        Enter an MFA code to complete sign-in
                      </label>
                    </div>
                    <Form.Item>
                      <Input
                        placeholder="MFA Code"
                        name="Code"
                        value={challengeAnswer}
                        onChange={e => setChallengeAnswer(e.target.value)}
                        onPressEnter={MFAAuthenticationOk}
                        autoFocus
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signin;